/**
 * Reading a click on a link: is this one the app should handle itself?
 *
 * Split out of `main.js` on 21 Aug 2026, when that file went past the length
 * guard. It was two jobs: decide which screen a URL means, and decide whether
 * a click was even asking for one. This is the second, and it is the half with
 * the browser conventions in it.
 *
 * Every rule below exists so the app does NOT take over a click the advisor
 * meant for their browser — a middle-click, a ⌘-click, a download, a link to
 * another site. Getting one of those wrong is how an in-app router quietly
 * breaks "open in a new tab".
 */

import { isAppPath } from '../lib/app-base.js'

/** @param {MouseEvent} event @returns {boolean} */
function isPlainLeftClick(event) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  )
}

/**
 * The in-app URL this click is asking for, or null when the browser should
 * handle it itself.
 *
 * @param {MouseEvent} event
 * @returns {URL|null}
 */
function internalTarget(event) {
  if (!isPlainLeftClick(event)) return null
  const el = /** @type {Element|null} */ (event.target)
  const anchor = el && typeof el.closest === 'function' ? el.closest('a[href]') : null
  if (!anchor) return null
  /* An advisor deliberately opening a screen in a new tab should get one. */
  if (anchor.hasAttribute('download') || anchor.getAttribute('target') === '_blank') return null

  let url
  try {
    url = new URL(anchor.getAttribute('href') || '', location.href)
  } catch {
    return null
  }
  if (url.origin !== location.origin) return null
  /* `/app` is the legacy program. `/v2/itinerary?trip=` is ours and keeps its
     query string, so the check is on the pathname only. */
  if (!isAppPath(url.pathname)) return null
  return url
}

export { internalTarget, isPlainLeftClick }
